import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MainLayout from "../../layouts/main/main_layout";
import { useLocation } from "react-router-dom";

interface HourlyReportResultLocationState {
  projectName?: string;
  date?: string;
  frName?: string;
  mode?: "single" | "all";
  headers?: string[];
  rows?: Array<Record<string, any>>;
  data?: Array<Record<string, any>>;
}

interface TableBlockProps {
  title: string;
  headers: string[];
  rows: Array<Record<string, any>>;
}

function TableBlock({ title, headers, rows }: TableBlockProps) {
  const theme = useTheme();

  return (
    <Box mb={4}>
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        {title}
      </Typography>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header, index) => (
                <TableCell
                  key={`${header}-${index}`}
                  align={index === 0 ? "left" : "right"}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 600,
                    fontSize: "13px",
                    borderBottom: "none",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length > 0 ? (
              rows.map((row, rowIndex) => (
                <TableRow
                  key={`${title}-${rowIndex}`}
                  sx={{
                    backgroundColor:
                      rowIndex % 2 === 0
                        ? theme.palette.background.default
                        : theme.palette.action.hover,
                  }}
                >
                  {headers.map((header, colIndex) => {
                    const value = row[header] ?? row[header.toLowerCase()] ?? row[header.replace(/\s+/g, "_").toLowerCase()] ?? "-";

                    return (
                      <TableCell
                        key={`${header}-${colIndex}`}
                        align={colIndex === 0 ? "left" : "right"}
                      >
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  No Data Available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

// ✅ Default fallback
const defaultHeaders = [
  "Hour",
  "Completed Surveys",
  "Pending Surveys",
  "Rejected Surveys",
  "Total Attempts",
  "Success Rate (%)",
];

const defaultRows = [
  {
    Hour: "09:00 - 10:00",
    "Completed Surveys": 5,
    "Pending Surveys": 2,
    "Rejected Surveys": 1,
    "Total Attempts": 8,
    "Success Rate (%)": "62.5%",
  },
  {
    Hour: "10:00 - 11:00",
    "Completed Surveys": 8,
    "Pending Surveys": 1,
    "Rejected Surveys": 1,
    "Total Attempts": 10,
    "Success Rate (%)": "80%",
  },
];

const formatHourLabel = (key: string) => {
  const map: Record<string, string> = {
    "7to8": "7:00 - 8:00",
    "8to9": "8:00 - 9:00",
    "9to10": "9:00 - 10:00",
    "10to11": "10:00 - 11:00",
    "11to12": "11:00 - 12:00",
    "12to13": "12:00 - 13:00",
    "13to14": "13:00 - 14:00",
    "14to15": "14:00 - 15:00",
    "15to16": "15:00 - 16:00",
    "16to17": "16:00 - 17:00",
    "17to18": "17:00 - 18:00",
    "18to19": "18:00 - 19:00",
    "19to20": "19:00 - 20:00",
    "20to21": "20:00 - 21:00",
    "21to22": "21:00 - 22:00",
  };

  return map[key] || key;
};

export default function HourlyReportResult() {
  const location = useLocation();
  const state = (location.state || {}) as HourlyReportResultLocationState;

  const projectTitle = state.projectName || "Project";
  const date = state.date || "-";
  const frName = state.frName || "-";

  const groupedData = (state.data && state.data.length > 0
    ? state.data
    : [
        {
          frName: frName || "FR - Default",
          headers: state.headers && state.headers.length ? state.headers : defaultHeaders,
          rows: state.rows && state.rows.length ? state.rows : defaultRows,
        },
      ]
  ).map((group: any) => {
    if (group.hourly_counts) {
      return {
        frName: group.fr_name || group.frName || frName,
        headers: ["Hour", "Count"],
        rows: Object.entries(group.hourly_counts).map(([key, value]) => ({
          Hour: formatHourLabel(key),
          Count: value ?? 0,
        })),
      };
    }

    return {
      frName: group.fr_name || group.frName || frName,
      headers: group.headers || defaultHeaders,
      rows: group.rows || defaultRows,
    };
  });

  return (
    <MainLayout>
      <Box px={2} py={3}>
        {/* Header */}
        <Box mb={2}>
          <Typography variant="h6" fontWeight={700}>
            {projectTitle}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {date}
          </Typography>

          <Typography variant="subtitle2" mt={1}>
            {frName}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* {groupedData.map((group, idx) => (
          <TableBlock
            key={`${group.frName}-${idx}`}
            title={group.frName}
            headers={group.headers || defaultHeaders}
            rows={group.rows || defaultRows}
          />
        ))} */}
      </Box>
    </MainLayout>
  );
}